import { useCallback, useEffect, useState } from 'react';
import { AUDIO_TUNABLES, INVENTED_AUDIO_COPY } from '../../platform/config/audio';
import type { FamilyErasureResult } from '../../storage/local/family-erasure';
import type { FamilyProfile, ParentSettings } from '../../shared/types/profiles';
import { useAppServices } from '../shell/services-context';

/**
 * Parent zone stub. Real dashboards, calibration restart and per-child controls arrive with
 * the phases that need them; what is real today is the settings round-trip through the
 * profile repository and the privacy statement, which is not a placeholder.
 */

const DEFAULT_PARENT_SETTINGS: ParentSettings = {
  defaultPlayerMode: 'SINGLE',
  narrationEnabled: true,
  narrationVolume: 1,
  reducedMotion: false,
  cameraEnabled: false,
  showParentSummaryAfterMission: true,
};

/**
 * What is left, in words a grown-up can act on — and never a number the delete cannot support
 * (P6R1-5).
 *
 * Round 1 counted surviving *keys* only, so a sitting whose database was demonstrably still on the
 * disk was reported as "0 records are still stored". Both kinds of survivor are named, and the
 * sentence is only reached when the delete was actually able to look (see `checked`).
 */
function describeSurvivors(erasure: FamilyErasureResult): string {
  const parts: string[] = [];
  if (erasure.remaining.length > 0) {
    parts.push(
      `${String(erasure.remaining.length)} ${erasure.remaining.length === 1 ? 'record is' : 'records are'}`,
    );
  }
  if (erasure.remainingDatabases.length > 0) {
    parts.push(
      `${String(erasure.remainingDatabases.length)} ${
        erasure.remainingDatabases.length === 1 ? 'database is' : 'databases are'
      }`,
    );
  }
  // Never "0 of anything": if nothing is nameable, say so rather than printing a zero.
  return parts.length === 0 ? 'something is' : parts.join(' and ');
}

function newFamily(settings: ParentSettings): FamilyProfile {
  const now = new Date().toISOString();
  return {
    id: 'family-local',
    childProfileIds: [],
    parentSettings: settings,
    createdAt: now,
    updatedAt: now,
  };
}

export function ParentPage() {
  const { repositories, narration, audio, eraseEverything: erase } = useAppServices();
  const [settings, setSettings] = useState<ParentSettings>(DEFAULT_PARENT_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [erasure, setErasure] = useState<FamilyErasureResult | null>(null);

  const eraseEverything = useCallback(async () => {
    setDeleting(true);
    try {
      const result = await erase();
      setErasure(result);
      setConfirming(false);
      // The screen must not go on showing settings that no longer exist anywhere.
      if (result.complete) setSettings(DEFAULT_PARENT_SETTINGS);
    } finally {
      setDeleting(false);
    }
  }, [erase]);

  useEffect(() => {
    void (async () => {
      const family = await repositories.profiles.getFamily();
      if (family !== null) setSettings(family.parentSettings);
      setLoaded(true);
    })();
  }, [repositories]);

  const persist = useCallback(
    async (next: ParentSettings) => {
      setSettings(next);
      narration.setEnabled(next.narrationEnabled);
      /**
       * Now, not next time (PS5-5).
       *
       * A grown-up reaching for a mute is reaching for it because of a sound that is playing.
       * Narration has taken effect immediately on this screen since Phase 0; sound is held to the
       * same standard rather than being the control that only applies to tomorrow's sitting.
       * Absent means the default, resolved here exactly as `resolveSessionLaunchDefaults` does it.
       */
      audio.setEnabled(next.soundEnabled ?? true);
      audio.setVolume(next.soundVolume ?? AUDIO_TUNABLES.defaultSoundVolume);
      const existing = await repositories.profiles.getFamily();
      const family: FamilyProfile =
        existing === null
          ? newFamily(next)
          : { ...existing, parentSettings: next, updatedAt: new Date().toISOString() };
      await repositories.profiles.saveFamily(family);
    },
    [audio, narration, repositories],
  );

  return (
    <section>
      <h1>Parent zone</h1>
      <p className="lede">Settings and the privacy promise. Children never see this screen.</p>

      <h2>Settings</h2>
      <div className="panel">
        <label>
          <input
            type="checkbox"
            checked={settings.narrationEnabled}
            disabled={!loaded}
            onChange={(event) => {
              void persist({ ...settings, narrationEnabled: event.target.checked });
            }}
          />{' '}
          Narration (Bip-Bop reads the instructions aloud)
        </label>
        <p className="notice" data-testid="narration-availability">
          {narration.available
            ? 'Your browser can speak the instructions.'
            : 'This browser has no speech voice available, so narration stays silent.'}
        </p>

        {/*
         * The family's standing answer to "is there sound", as opposed to the setup screen's
         * answer for one evening (PS5-5). Missing means on: a record written before this build
         * says nothing, and nothing is not a choice to be quiet.
         */}
        <label>
          <input
            type="checkbox"
            checked={settings.soundEnabled ?? true}
            disabled={!loaded}
            onChange={(event) => {
              void persist({ ...settings, soundEnabled: event.target.checked });
            }}
          />{' '}
          {INVENTED_AUDIO_COPY.soundLabel}
        </label>

        <label>
          <input
            type="checkbox"
            checked={settings.reducedMotion}
            disabled={!loaded}
            onChange={(event) => {
              void persist({ ...settings, reducedMotion: event.target.checked });
            }}
          />{' '}
          Calmer motion (fewer moving effects)
        </label>
      </div>

      <h2>Privacy</h2>
      <div className="panel">
        <ul>
          <li>Everything runs on this device. There is no account and no server.</li>
          <li>
            When your child plays with the camera, the picture is used to work out movement and is
            never recorded, uploaded or used to recognise a face.
          </li>
          <li>No video, no photos, no voice and no body measurements are ever stored.</li>
          <li>No adverts, no tracking, and no links that take a child off this app.</li>
          <li>Progress lives in this browser only. Clearing site data removes it, permanently.</li>
        </ul>
      </div>

      <h2>Delete everything</h2>
      <div className="panel">
        {/*
         * BL-003 / PS6-3: a physical delete, and one that checks (see `family-erasure.ts`).
         *
         * Two steps rather than one, because this cannot be undone and because a child could reach
         * this screen by accident. The result is reported honestly in both directions — a delete
         * that could not finish says so, with the number of records it could not remove, rather
         * than telling a grown-up their family's data is gone when it is not.
         *
         * COPY FLAG (grown-up strings): every string in this panel.
         */}
        <p>
          This removes every profile, every bit of progress and everything else this app has saved
          in this browser. It cannot be undone.
        </p>
        {confirming ? (
          <p>
            <button
              type="button"
              className="button button--primary"
              data-testid="confirm-delete-everything"
              disabled={deleting}
              onClick={() => {
                void eraseEverything();
              }}
            >
              Yes, delete everything
            </button>{' '}
            <button
              type="button"
              className="button"
              data-testid="cancel-delete-everything"
              disabled={deleting}
              onClick={() => {
                setConfirming(false);
              }}
            >
              Keep it
            </button>
          </p>
        ) : (
          <p>
            <button
              type="button"
              className="button"
              data-testid="delete-everything"
              onClick={() => {
                setErasure(null);
                setConfirming(true);
              }}
            >
              Delete everything saved here
            </button>
          </p>
        )}
        {erasure === null ? null : (
          <p
            className="notice"
            role="status"
            data-testid="delete-everything-result"
            data-complete={erasure.complete ? 'true' : 'false'}
            data-checked={erasure.checked ? 'true' : 'false'}
          >
            {erasure.complete
              ? 'Everything saved in this browser has been deleted, and checked.'
              : erasure.checked
                ? `Some of it could not be removed: ${describeSurvivors(erasure)} still stored. Closing other tabs of this app and trying again usually helps.`
                : 'The deletion ran, but this browser would not let us check what is left. Clearing site data for this page in your browser settings removes everything for certain.'}
          </p>
        )}
      </div>
    </section>
  );
}
